import React from 'react';
import {
  IconGripVertical,
  IconChevronDown,
  IconBuilding,
  IconClipboardCheck,
  IconAlertTriangle,
} from '@tabler/icons-react';
import type { ChipColor } from '../check-in/sessionDetail/sessionDetailHelpers';
import './JobRow.css';
import './CheckInRow.css';

interface CheckInRowProps {
  sessionNumber: string;
  date: string;
  customer: string;
  /** Full "Business - Full Name" hover text shown on the customer name. */
  customerTitle?: string;
  /** Shows a building icon before the customer name for business customers. */
  isBusiness?: boolean;
  title: string;
  /** Secondary line under the title — e.g. "In-Store · 2 devices". */
  subtitle?: string;
  /** Lifecycle status label, e.g. "Awaiting Intake". */
  statusLabel: string;
  /** Lifecycle chip colour — drives the stripe, dot, and status text. */
  statusColor: ChipColor;
  /** Red attention marker when the session needs action. */
  requiresAttention?: boolean;
  /** Preliminary charges, pre-formatted as AUD; empty when there are none. */
  chargesText?: string;
  onExpand?: () => void;
  /** Rotates the chevron to indicate the row's expansion panel is open. */
  expanded?: boolean;
  /** ── Optional drag-and-drop wiring (set by SortableRow) ── */
  /** Ref for the draggable node — goes on the card. */
  setNodeRef?: (el: HTMLElement | null) => void;
  /** Ref for the drag activator — goes on the handle. */
  setActivatorNodeRef?: (el: HTMLElement | null) => void;
  /** Listeners + attributes for the drag handle. */
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
  /** Right-click the drag handle (opens the reassign menu). */
  onHandleContextMenu?: (e: React.MouseEvent) => void;
  /** Transform/transition style applied to the card while sorting. */
  style?: React.CSSProperties;
  /** Omit the built-in drag handle (the parent renders it externally). */
  hideHandle?: boolean;
}

/**
 * A single check-in session row: drag handle + a lifecycle-coloured striped
 * card with the session number, customer, title, a (display-only) lifecycle
 * status pill, preliminary charges, and an expand chevron. The full session
 * detail — Open Session, payment/intake chips, draft jobs, retry, cancel —
 * lives in the expansion panel (CheckInPanelSection). Presentational; mirrors
 * JobRow's column layout so rows line up within a technician group.
 */
export const CheckInRow: React.FC<CheckInRowProps> = ({
  sessionNumber,
  date,
  customer,
  customerTitle,
  isBusiness,
  title,
  subtitle,
  statusLabel,
  statusColor,
  requiresAttention,
  chargesText,
  onExpand,
  expanded,
  setNodeRef,
  setActivatorNodeRef,
  dragHandleProps,
  onHandleContextMenu,
  style,
  hideHandle,
}) => (
  <div className="job-row">
    {!hideHandle && (
      <div className="job-row__handle" ref={setActivatorNodeRef} {...dragHandleProps} onContextMenu={onHandleContextMenu}>
        <IconGripVertical size={18} stroke={1.5} />
      </div>
    )}
    <div
      className={`checkin-row checkin-row--${statusColor}`}
      ref={setNodeRef}
      style={onExpand ? { ...style, cursor: 'pointer' } : style}
      onClick={onExpand}
    >
      <div className="checkin-row__stripe" />
      <div className="checkin-row__inner">
        <div className="checkin-row__meta">
          <span className="checkin-row__session">{sessionNumber}</span>
          <span className="checkin-row__date">{date}</span>
        </div>
        <div className="checkin-row__custblock">
          <div className="checkin-row__custname">
            {isBusiness && (
              <IconBuilding className="checkin-row__business-icon" size={13} stroke={1.5} />
            )}
            <span className="checkin-row__customer" title={customerTitle ?? customer}>
              {customer}
            </span>
          </div>
        </div>
        <div className="checkin-row__titleblock">
          <span className="checkin-row__title">{title}</span>
          {subtitle && <span className="checkin-row__subtitle">{subtitle}</span>}
        </div>
        <div className="checkin-row__endblock">
          <div className="checkin-row__endtop">
            {requiresAttention && (
              <span className="checkin-row__attention" title="Needs attention">
                <IconAlertTriangle size={14} stroke={1.5} />
              </span>
            )}
            <IconClipboardCheck className="checkin-row__type-icon" size={18} stroke={1.5} />
            <span className="checkin-row__status">
              <span className="checkin-row__status-dot" />
              {statusLabel}
            </span>
          </div>
          {chargesText && <div className="checkin-row__charges">{chargesText}</div>}
        </div>
        <IconChevronDown
          className="checkin-row__chevron"
          size={20}
          stroke={1.5}
          style={{ transform: expanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s ease' }}
        />
      </div>
    </div>
  </div>
);
